import { Button, Icons, Input } from '@commenda/kakashi';
import { useCallback } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { RecipientPicker } from './RecipientPicker';
import type { RecipientsFormValues } from './types';

type SelectRecipientsStepProps = {
  companyId?: number | null;
};

export function SelectRecipientsStep({ companyId }: SelectRecipientsStepProps) {
  const { control, register, formState, getValues, setValue } =
    useFormContext<RecipientsFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'recipients',
  });
  const { errors } = formState;

  const watchedRecipients = useWatch({ control, name: 'recipients' });
  const excludedEmails = (watchedRecipients ?? [])
    .map((recipient) => recipient?.email?.trim().toLowerCase() ?? '')
    .filter((email) => email.length > 0);

  const handleSelectPerson = useCallback(
    (recipient: { name: string; email: string }) => {
      const current = getValues('recipients');
      const alreadyAdded = current.some(
        (row) =>
          row.email.trim().toLowerCase() === recipient.email.toLowerCase()
      );
      if (alreadyAdded) return;

      const emptyIndex = current.findIndex(
        (row) => !row.name.trim() && !row.email.trim()
      );
      if (emptyIndex >= 0) {
        setValue(`recipients.${emptyIndex}.name`, recipient.name, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`recipients.${emptyIndex}.email`, recipient.email, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        append(recipient);
      }
    },
    [append, getValues, setValue]
  );

  return (
    <>
      {companyId != null && (
        <RecipientPicker
          companyId={companyId}
          excludedEmails={excludedEmails}
          onSelect={handleSelectPerson}
        />
      )}
      {fields.map((field, index) => (
        <div key={field.id} className="flex items-start gap-2">
          <div className="flex-1 flex flex-col gap-2">
            <Input
              placeholder="Full name"
              {...register(`recipients.${index}.name`)}
              error={errors?.recipients?.[index]?.name?.message}
            />
            <Input
              placeholder="Email address"
              type="email"
              {...register(`recipients.${index}.email`)}
              error={errors?.recipients?.[index]?.email?.message}
            />
          </div>
          {fields.length > 1 && (
            <Button
              variant="tertiary"
              size="sm"
              type="button"
              onClick={() => remove(index)}
              className="mt-1"
            >
              <Icons.Trash01 className="size-4" />
            </Button>
          )}
        </div>
      ))}
      <Button
        variant="secondary"
        size="sm"
        type="button"
        onClick={() => append({ name: '', email: '' })}
        className="self-start"
      >
        <Icons.Plus className="size-4 mr-1" />
        Add recipient
      </Button>
    </>
  );
}
